import { useRef, useState, useCallback } from 'react';

/**
 * Generate a random 6-digit session ID
 */
function generateSessionId() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

/**
 * Format date as YYYY-mm-DD
 */
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Hook for recording video from a MediaStream using MediaRecorder API
 * Records in 1-second chunks and uploads each chunk immediately.
 *
 * File naming: recording-YYYY-mm-DD-[sessionId]-XXX.webm
 * Where XXX is a 3-digit sequence number (000-999)
 *
 * @param {Object} options
 * @param {string} options.mimeType - Preferred MIME type (default: video/webm)
 * @param {number} options.videoBitsPerSecond - Video bitrate (default: 2500000 = 2.5 Mbps)
 * @returns {Object} Recording state and controls
 */
export function useMediaRecorder(options = {}) {
  const {
    mimeType: preferredMimeType = 'video/webm',
    videoBitsPerSecond = 2500000
  } = options;

  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);
  const [uploadedChunks, setUploadedChunks] = useState(0);
  const [sessionId, setSessionId] = useState(null);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startTimeRef = useRef(null);
  const durationTimerRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const sessionIdRef = useRef(null);
  const sessionDateRef = useRef(null);
  const uploadUrlRef = useRef(null);
  const exerciseTypeRef = useRef(null);
  const pendingUploadsRef = useRef([]);
  const mimeTypeRef = useRef(null);

  /**
   * Get the best supported MIME type
   */
  const getSupportedMimeType = useCallback(() => {
    const mimeTypes = [
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
      'video/mp4'
    ];

    // Try preferred type first
    if (MediaRecorder.isTypeSupported(preferredMimeType)) {
      return preferredMimeType;
    }

    for (const type of mimeTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    return null;
  }, [preferredMimeType]);

  /**
   * Upload a single chunk
   */
  const uploadChunk = useCallback(async (blob, chunkIndex, sessionId, sessionDate, exerciseType, uploadUrl) => {
    const sequenceNum = String(chunkIndex).padStart(3, '0');
    const prefix = exerciseType || 'unknown';
    const filename = `${prefix}-recordingv2-${sessionDate}-${sessionId}-${sequenceNum}.webm`;

    const formData = new FormData();
    formData.append('video', blob, filename);
    formData.append('exercise_type', exerciseType || 'unknown');
    formData.append('session_id', sessionId);
    formData.append('chunk_index', chunkIndex);
    formData.append('filename', filename);

    try {
      const response = await fetch(uploadUrl, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }

      const result = await response.json();
      console.log(`[useMediaRecorder] Chunk ${sequenceNum} uploaded:`, result.filename);
      return result;
    } catch (err) {
      console.error(`[useMediaRecorder] Chunk ${sequenceNum} upload failed:`, err);
      throw err;
    }
  }, []);

  /**
   * Start recording from a stream with streaming uploads
   * @param {MediaStream} stream - The media stream to record
   * @param {Object} uploadConfig - Upload configuration
   * @param {string} uploadConfig.uploadUrl - URL to upload chunks to
   * @param {string} uploadConfig.exerciseType - Type of exercise
   */
  const startRecording = useCallback((stream, uploadConfig = {}) => {
    if (!stream) {
      setError('No stream provided for recording');
      return false;
    }

    if (!MediaRecorder) {
      setError('MediaRecorder API is not supported in this browser');
      return false;
    }

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      setError('No supported video MIME types found');
      return false;
    }

    try {
      // Reset state
      chunksRef.current = [];
      chunkIndexRef.current = 0;
      pendingUploadsRef.current = [];
      setRecordedBlob(null);
      setError(null);
      setUploadedChunks(0);

      // Generate session ID and date
      const newSessionId = generateSessionId();
      const newSessionDate = formatDate(new Date());
      sessionIdRef.current = newSessionId;
      sessionDateRef.current = newSessionDate;
      uploadUrlRef.current = uploadConfig.uploadUrl;
      exerciseTypeRef.current = uploadConfig.exerciseType;
      mimeTypeRef.current = mimeType;

      setSessionId(newSessionId);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond
      });

      mediaRecorder.ondataavailable = async (event) => {
        if (event.data && event.data.size > 0) {
          const chunkIndex = chunkIndexRef.current;
          chunkIndexRef.current++;

          // Store chunk for final blob
          chunksRef.current.push(event.data);

          // Upload chunk if URL is provided
          if (uploadUrlRef.current) {
            const uploadPromise = uploadChunk(
              event.data,
              chunkIndex,
              sessionIdRef.current,
              sessionDateRef.current,
              exerciseTypeRef.current,
              uploadUrlRef.current
            ).then(() => {
              setUploadedChunks(prev => prev + 1);
            }).catch(err => {
              console.error('[useMediaRecorder] Chunk upload error:', err);
            });

            pendingUploadsRef.current.push(uploadPromise);
          }
        }
      };

      mediaRecorder.onstop = async () => {
        // Wait for all pending uploads to complete
        if (pendingUploadsRef.current.length > 0) {
          console.log(`[useMediaRecorder] Waiting for ${pendingUploadsRef.current.length} pending uploads...`);
          await Promise.allSettled(pendingUploadsRef.current);
        }

        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordedBlob(blob);
        setIsRecording(false);
        setIsPaused(false);

        // Clear duration timer
        if (durationTimerRef.current) {
          clearInterval(durationTimerRef.current);
          durationTimerRef.current = null;
        }

        console.log(`[useMediaRecorder] Recording stopped. Session: ${sessionIdRef.current}, Chunks: ${chunkIndexRef.current}`);
      };

      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event.error);
        setError(`Recording error: ${event.error?.message || 'Unknown error'}`);
        setIsRecording(false);
      };

      mediaRecorderRef.current = mediaRecorder;

      // Request data every second (1000ms) for chunk-based uploads
      mediaRecorder.start(1000);
      startTimeRef.current = Date.now();
      setIsRecording(true);
      setDuration(0);

      // Update duration every second
      durationTimerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
        }
      }, 1000);

      console.log(`[useMediaRecorder] Started recording. Session: ${newSessionId}, Date: ${newSessionDate}, MIME: ${mimeType}`);
      return true;
    } catch (err) {
      console.error('Failed to start recording:', err);
      setError(`Failed to start recording: ${err.message}`);
      return false;
    }
  }, [getSupportedMimeType, videoBitsPerSecond, uploadChunk]);

  /**
   * Stop recording
   */
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      console.log('[useMediaRecorder] Stopped recording');
    }
  }, []);

  /**
   * Pause recording
   */
  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      console.log('[useMediaRecorder] Paused recording');
    }
  }, []);

  /**
   * Resume recording
   */
  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      console.log('[useMediaRecorder] Resumed recording');
    }
  }, []);

  /**
   * Upload the full recorded blob to the server (legacy method)
   * @param {Object} params - Upload parameters
   * @param {string} params.uploadUrl - URL to upload to
   * @param {string} params.exerciseType - Type of exercise
   * @param {number} params.repCount - Number of reps
   * @param {Object} params.metadata - Additional metadata
   * @returns {Promise<Object>} Upload result
   */
  const uploadRecording = useCallback(async ({ uploadUrl, exerciseType, repCount, metadata = {} } = {}) => {
    if (!recordedBlob) {
      throw new Error('No recording available to upload');
    }

    if (!uploadUrl) {
      throw new Error('No upload URL provided');
    }

    const formData = new FormData();
    formData.append('video', recordedBlob, `recording-${Date.now()}.webm`);
    formData.append('exercise_type', exerciseType || 'unknown');
    formData.append('rep_count', repCount || 0);
    formData.append('duration', duration);
    formData.append('metadata', JSON.stringify(metadata));

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    const result = await response.json();
    console.log('[useMediaRecorder] Upload successful:', result);
    return result;
  }, [recordedBlob, duration]);

  /**
   * Clear the recorded blob
   */
  const clearRecording = useCallback(() => {
    setRecordedBlob(null);
    chunksRef.current = [];
    chunkIndexRef.current = 0;
    setUploadedChunks(0);
    setSessionId(null);
  }, []);

  /**
   * Cleanup resources
   */
  const cleanup = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    chunksRef.current = [];
    chunkIndexRef.current = 0;
    pendingUploadsRef.current = [];
    setRecordedBlob(null);
    setIsRecording(false);
    setIsPaused(false);
    setDuration(0);
    setUploadedChunks(0);
    setSessionId(null);
  }, []);

  return {
    // State
    isRecording,
    isPaused,
    recordedBlob,
    error,
    duration,
    uploadedChunks,
    sessionId,

    // Controls
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    uploadRecording,
    clearRecording,
    cleanup,

    // Utilities
    getSupportedMimeType,
    isSupported: !!MediaRecorder
  };
}
